// apps/web/src/lib/srs.ts
// ─────────────────────────────────────────────────────────────────────────
// SRS V2 + GAMIFICATION SHIM — sync, localStorage-backed.
//
// The core @nisk/core/srs package is pure (no platform code). This
// shim wraps the pure functions with sync localStorage I/O and adds:
//   • silent V1 → V2 migration on every load
//   • a one-time "Flashcard progress upgraded" toast flag
//   • a cardId → lessonId resolver + getLessonFlashcards built from the
//     curriculum (keeps the core engine pure: it never imports learning data)
//
// TODO(post-migration): port call sites to async Dexie reads, then delete.
// ─────────────────────────────────────────────────────────────────────────

import {
	computeNextRecord,
	newCardRecord,
	previewIntervals,
	formatDueDelta,
	isDue,
	isDueCard,
	isMastered,
	isLessonFlashcardsMastered,
	migrateSrsRecord,
	inferMasteryLevel,
	XP_REWARD,
	XP_BONUS,
	getLevelInfo,
	levelLabel,
	applyStudyToday,
	emptyStreak,
	normalizeStreak,
	MAX_STREAK_FREEZES,
	streakDayISO,
	type CardSRSRecord,
	type SRSRating,
	type CardState,
	type StreakData,
	type LevelInfo,
} from '@nisk/core/srs';
import { LEARNING_MODULES, type Flashcard } from '@nisk/core/learning';
import {
	loadPreferences,
	type UserFlashcardPreference,
} from './flashcardPrefs';
import {
	getLessonFlashcardMasterySummary,
	type LessonFlashcardMasterySummary,
} from './flashcardMasteryPolicy';

// The active-card floor and the "solid" rule are NOT defined here. They live
// in the single policy layer (flashcardMasteryPolicy.ts). No cycle: the policy
// imports only types from flashcardPrefs + isMastered from core, never srs.ts.

// ── Storage keys (must match migrateLocalStorage.ts so data survives) ────
const SRS_PREFIX = 'nis.srs.';
const XP_KEY = 'nis.gamification.xp';
const STREAK_KEY = 'nis.gamification.streak';
const UPGRADE_TOAST_KEY = 'nis.srs.upgradeToastShown';
const LESSON_FC_PREFIX = 'nis.lessonFlashcards.';

// Same-tab progress signal (mirrors useProgress.ts so Learn views refresh).
const PROGRESS_EVENT = 'nis:progress';
function emitProgressChange(): void {
	try {
		window.dispatchEvent(new Event(PROGRESS_EVENT));
	} catch {
		/* noop */
	}
}

function srsKey(cardId: string): string {
	return `${SRS_PREFIX}${cardId}`;
}

// ── SRS records (migrate on read) ─────────────────────────────────────────
export function loadRecord(cardId: string): CardSRSRecord {
	try {
		const raw = localStorage.getItem(srsKey(cardId));
		if (raw) {
			const parsed: unknown = JSON.parse(raw);
			const migrated = migrateSrsRecord(cardId, parsed);
			// If migration changed the shape, persist the upgrade once so we
			// don't re-run it every read.
			if (
				typeof parsed === 'object' &&
				parsed !== null &&
				(parsed as { schedulerVersion?: number }).schedulerVersion !== 2
			) {
				saveRecord(migrated);
				markUpgradePending();
			}
			return migrated;
		}
	} catch {
		/* noop */
	}
	return newCardRecord(cardId);
}

export function saveRecord(
	recordOrId: CardSRSRecord | string,
	maybeRecord?: CardSRSRecord,
): void {
	const record =
		typeof recordOrId === 'string' && maybeRecord
			? maybeRecord
			: (recordOrId as CardSRSRecord);
	try {
		localStorage.setItem(srsKey(record.cardId), JSON.stringify(record));
	} catch {
		/* noop */
	}
}

// True only if the user has actually studied this card (a record was
// persisted). A never-touched card has no stored record; loadRecord() would
// synthesize a fresh "new" record whose dueAt is now, which makes isDue()
// report the entire untouched curriculum as "due". This guards against that.
export function hasRecord(cardId: string): boolean {
	try {
		return localStorage.getItem(srsKey(cardId)) !== null;
	} catch {
		return false;
	}
}

// ── One-time upgrade toast ────────────────────────────────────────────────
// loadRecord sets a "pending" marker when it migrates anything. The UI calls
// consumeUpgradeToast() once on mount; it returns true at most once ever.
function markUpgradePending(): void {
	try {
		if (localStorage.getItem(UPGRADE_TOAST_KEY) === null) {
			localStorage.setItem(UPGRADE_TOAST_KEY, 'pending');
		}
	} catch {
		/* noop */
	}
}

export function consumeUpgradeToast(): boolean {
	try {
		if (localStorage.getItem(UPGRADE_TOAST_KEY) === 'pending') {
			localStorage.setItem(UPGRADE_TOAST_KEY, 'shown');
			return true;
		}
	} catch {
		/* noop */
	}
	return false;
}

// ── XP / Levels ──────────────────────────────────────────────────────────
export function loadXP(): number {
	try {
		const raw = localStorage.getItem(XP_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			return typeof parsed === 'number' ? parsed : (parsed.total ?? 0);
		}
	} catch {
		/* noop */
	}
	return 0;
}

// ── Streak ───────────────────────────────────────────────────────────────
export function loadStreak(): StreakData {
	try {
		const raw = localStorage.getItem(STREAK_KEY);
		if (raw) return normalizeStreak(JSON.parse(raw));
	} catch {
		/* noop */
	}
	return emptyStreak();
}

function saveStreak(streak: StreakData): void {
	try {
		localStorage.setItem(STREAK_KEY, JSON.stringify(streak));
	} catch {
		/* noop */
	}
}

export function recordStudyToday(): StreakData {
	const current = loadStreak();
	const next = applyStudyToday(current);
	if (next !== current) saveStreak(next);
	return next;
}

/**
 * Add one Streak Freeze (purchase side-effect — the GEM SPEND happens in the
 * economy layer; this only mutates inventory). Clamped to MAX_STREAK_FREEZES.
 * Emits the same-tab progress signal so useStreak consumers refresh.
 * Consumption is NOT here — core applyStudyToday auto-consumes freezes when
 * they bridge missed days, keeping one writer for all streak math.
 */
export function addStreakFreeze(): StreakData {
	const current = loadStreak();
	if (current.freezesOwned >= MAX_STREAK_FREEZES) return current;
	const next: StreakData = {
		...current,
		freezesOwned: current.freezesOwned + 1,
	};
	saveStreak(next);
	emitProgressChange();
	return next;
}

// ── Curriculum resolvers (cardId ⇄ lessonId) ───────────────────────────────
// Built once from LEARNING_MODULES. The core engine stays pure; this is the
// app-side glue that lets us award lesson-owned mastery.
interface CardLessonRef {
	lessonId: string;
	moduleId: string;
}

let _cardToLesson: Map<string, CardLessonRef> | null = null;
let _lessonCards: Map<string, Flashcard[]> | null = null;

function buildIndexes(): void {
	const cardToLesson = new Map<string, CardLessonRef>();
	const lessonCards = new Map<string, Flashcard[]>();
	for (const mod of LEARNING_MODULES) {
		for (const lesson of mod.lessons) {
			lessonCards.set(lesson.id, lesson.flashcards);
			for (const card of lesson.flashcards) {
				cardToLesson.set(card.id, {
					lessonId: lesson.id,
					moduleId: mod.id,
				});
			}
		}
	}
	_cardToLesson = cardToLesson;
	_lessonCards = lessonCards;
}

function cardToLesson(): Map<string, CardLessonRef> {
	if (!_cardToLesson) buildIndexes();
	return _cardToLesson as Map<string, CardLessonRef>;
}

function lessonCards(): Map<string, Flashcard[]> {
	if (!_lessonCards) buildIndexes();
	return _lessonCards as Map<string, Flashcard[]>;
}

export function getLessonIdForCard(cardId: string): string | null {
	return cardToLesson().get(cardId)?.lessonId ?? null;
}

export function getLessonFlashcards(lessonId: string): Flashcard[] {
	return lessonCards().get(lessonId) ?? [];
}

// ── Lesson flashcard checkpoint / perfect / retained (idempotent writes) ────
// Storage keys. The legacy `nis.lessonFlashcards.{id}` boolean is REPURPOSED
// to mean "checkpoint passed" (it already gated lesson progress, which is now
// the checkpoint's job — so existing data keeps the right meaning). Perfect
// and retained get their own flags so they don't collide.
function lessonFcKey(lessonId: string): string {
	return `${LESSON_FC_PREFIX}${lessonId}`; // checkpoint flag (legacy key)
}
function lessonFcPerfectKey(lessonId: string): string {
	return `${LESSON_FC_PREFIX}${lessonId}.perfect`;
}
function lessonFcRetainedKey(lessonId: string): string {
	return `${LESSON_FC_PREFIX}${lessonId}.retained`;
}

function readBoolFlag(key: string): boolean {
	try {
		return localStorage.getItem(key) === 'true';
	} catch {
		return false;
	}
}

/** Has this lesson's short-term flashcard CHECKPOINT been passed? */
export function isLessonFlashcardCheckpointFlag(lessonId: string): boolean {
	return readBoolFlag(lessonFcKey(lessonId));
}

/** Have all active anchors been solid at once (3/3)? */
export function isLessonFlashcardsPerfectFlag(lessonId: string): boolean {
	return readBoolFlag(lessonFcPerfectKey(lessonId));
}
/** Have all active anchors reached long-term retention (engine isMastered)? */
export function isLessonFlashcardsRetainedFlag(lessonId: string): boolean {
	return readBoolFlag(lessonFcRetainedKey(lessonId));
}

/** Build the policy summary for the lesson that owns `cardId`. */
export function getLessonSummaryForCard(
	cardId: string,
): LessonFlashcardMasterySummary | null {
	const lessonId = getLessonIdForCard(cardId);
	if (!lessonId) return null;
	const cards = getLessonFlashcards(lessonId);
	if (cards.length === 0) return null;
	const ids = cards.map((c) => c.id);
	const prefs: Record<string, UserFlashcardPreference | undefined> =
		loadPreferences(ids);
	const srs = Object.fromEntries(ids.map((id) => [id, loadRecord(id)]));
	return getLessonFlashcardMasterySummary({
		flashcards: cards,
		preferencesByCardId: prefs,
		srsStateByCardId: srs,
	});
}

export interface LessonFlashcardEvaluation {
	lessonId: string | null;
	/** Checkpoint passed for the FIRST time this call (short-term proof). */
	newlyCheckpointed: boolean;
	/** Hit 3/3 solid for the FIRST time this call. */
	newlyPerfect: boolean;
	/** Reached long-term retention for the FIRST time this call. */
	newlyRetained: boolean;
}

/**
 * Re-evaluates the lesson that owns `cardId` against the SINGLE policy layer
 * and persists any newly-reached tier. Each tier is idempotent (its flag flips
 * once), so callers never double-fire toasts / XP. Returns which tiers were
 * newly reached THIS call so the caller can fire the right reward(s).
 *
 * Tiers (spec §5):
 *   • checkpoint → ≥ floor active cards SOLID + all active reviewed. Gates
 *     lesson progress. No time-gate.
 *   • perfect    → ALL active cards solid (the 3/3 bonus).
 *   • retained   → ALL active cards long-term mastered (engine isMastered).
 */
export function evaluateLessonFlashcardMastery(
	cardId: string,
): LessonFlashcardEvaluation {
	const lessonId = getLessonIdForCard(cardId);
	const base: LessonFlashcardEvaluation = {
		lessonId,
		newlyCheckpointed: false,
		newlyPerfect: false,
		newlyRetained: false,
	};
	if (!lessonId) return base;

	const summary = getLessonSummaryForCard(cardId);
	if (!summary) return base;

	let { newlyCheckpointed, newlyPerfect, newlyRetained } = base;

	// Checkpoint (short-term) — the lesson-progress gate.
	if (
		summary.checkpointPassed &&
		!isLessonFlashcardCheckpointFlag(lessonId)
	) {
		try {
			localStorage.setItem(lessonFcKey(lessonId), 'true');
			newlyCheckpointed = true;
		} catch {
			/* noop */
		}
	}

	// Perfect (3/3 solid) — bonus.
	if (summary.perfectMemory && !isLessonFlashcardsPerfectFlag(lessonId)) {
		try {
			localStorage.setItem(lessonFcPerfectKey(lessonId), 'true');
			newlyPerfect = true;
		} catch {
			/* noop */
		}
	}

	// Retained (long-term) — all active cards at the engine's isMastered bar.
	const allActiveRetained =
		summary.activeCards > 0 &&
		summary.retainedActiveCards === summary.activeCards;
	if (allActiveRetained && !isLessonFlashcardsRetainedFlag(lessonId)) {
		try {
			localStorage.setItem(lessonFcRetainedKey(lessonId), 'true');
			newlyRetained = true;
		} catch {
			/* noop */
		}
	}

	if (newlyCheckpointed || newlyPerfect || newlyRetained) {
		emitProgressChange();
	}

	return { lessonId, newlyCheckpointed, newlyPerfect, newlyRetained };
}

// ── Re-exports (so callers can keep importing from one place) ────────────
export {
	computeNextRecord,
	newCardRecord,
	previewIntervals,
	formatDueDelta,
	isDue,
	isDueCard,
	isMastered,
	isLessonFlashcardsMastered,
	migrateSrsRecord,
	inferMasteryLevel,
	XP_REWARD,
	XP_BONUS,
	getLevelInfo,
	levelLabel,
	emptyStreak,
	normalizeStreak,
	MAX_STREAK_FREEZES,
	streakDayISO,
};

export type {
	CardSRSRecord,
	SRSRating,
	CardState,
	StreakData,
	LevelInfo,
	Flashcard,
};
